import { RepeatableSpace } from '../types/RepeatableSpace';
import { RepeatableSpaceRow } from '../types/RepeatableSpaceRow';
import { Space } from '../types/Space';
import { spaceFactory } from './spaceFactory';

/**
 * Factory function to create RepeatableSpace object
 * @param baseKey string key, NEVER USE DYNAMIC STRINGS
 * @param defaultSpaces string key array for default spaces
 * @returns RepeatableSpace object
 */
export const repeatableSpaceFactory = (
  baseKey: string,
  defaultSpaces?: string[],
  baseVisible = true,
) => {
  const rows: RepeatableSpaceRow[] = [];

  /**
   * Add a single row
   * @param key row key
   */
  const addRow = (key: string) => {
    const spaces: Record<string, Space> = {};
    const rowKey = `${baseKey}.${key}`;

    rows.push({
      key: rowKey,
      spaces,
      get: (space: string) => {
        return spaces[space];
      },
      getMany: (spaceKeys: string[]) => {
        return spaceKeys.map((space) => spaces[space]);
      },
    });

    const addSpace = (spaceKey: string, spaceVisible = true) => {
      spaces[spaceKey] = spaceFactory(`${rowKey}.${spaceKey}`, spaceVisible).get();
      return { addSpace };
    };

    const addSpaces = (spaceKeys: string[]) => {
      spaceKeys.forEach((space) => addSpace(space));
    };

    if (defaultSpaces) addSpaces(defaultSpaces);

    return { addRow, addSpace, addSpaces };
  };

  /**
   * Add several rows at the same time, all with the provided spaces
   * @param rowKeys array of rows to add
   * @param spaceKeys array of spaces to add to each row
   */
  const addRows = (rowKeys: string[], spaceKeys?: string[]) => {
    rowKeys.forEach((row) => {
      const res = addRow(row);
      if (spaceKeys) res.addSpaces(spaceKeys);
    });
  };

  /**
   * Get the resulting RepeatableSpace
   * @returns RepeatableSpace
   */
  const get = (): RepeatableSpace => {
    return {
      ...spaceFactory(baseKey, baseVisible).get(),
      rows,
    };
  };

  return { addRow, addRows, get };
};
